import { useMemo } from "react";
import { useStore } from "@/contexts/StoreContext";
import { useProjectDetails } from "@/api/api";
import type { ProjectDetailsResponse } from "@/types/project";
import type { ProjectListItem } from "@/types/api";
import { computeProjectPermissions, type ProjectPermissions } from "@/utils/projectPermissions";

/** The logged-in user's email (from `/user-info`, populated into `userStore` at app start), or null before it loads. */
export function useCurrentUserEmail(): string | null {
  const { userStore } = useStore();
  return userStore.userInfo?.email ?? null;
}

/**
 * Owner-lock write permissions (`isOwner`, `isLocked`, `canWrite`) for the current project.
 *
 * Self-sufficient by default: mirrors the `mobxProjectDetails` / `useProjectDetails` /
 * "query wins, mobx as fallback" lookup pattern already used by every ProjectPage descendant
 * (see e.g. CharactersPanel, ScriptPanel), so any component under ProjectPage can call this
 * with no arguments and no prop drilling. Pass `projectDetailsOverride` only when the caller
 * already resolved the effective details itself (e.g. ProjectPage) to avoid a redundant lookup.
 */
export function useProjectPermissions(projectDetailsOverride?: ProjectDetailsResponse | null): ProjectPermissions {
  const currentUserEmail = useCurrentUserEmail();
  const { actionStore } = useStore();
  const mobxProjectDetails = actionStore.projectDetails;
  const projectId = projectDetailsOverride?.project_id ?? mobxProjectDetails?.project_id ?? null;
  const projectDetailsQuery = useProjectDetails(projectDetailsOverride === undefined ? projectId : null);

  const effectiveProjectDetails = projectDetailsOverride ?? projectDetailsQuery.data ?? mobxProjectDetails ?? null;

  return useMemo(
    () =>
      computeProjectPermissions(
        currentUserEmail,
        effectiveProjectDetails?.user_name,
        effectiveProjectDetails?.is_locked,
        effectiveProjectDetails?.locked_by
      ),
    [currentUserEmail, effectiveProjectDetails?.user_name, effectiveProjectDetails?.is_locked, effectiveProjectDetails?.locked_by]
  );
}

/**
 * Same computation as `useProjectPermissions`, for a project-list row (`GET /projects`
 * summary shape) instead of full project details - e.g. ProjectCard's lock badge / disabled
 * open-to-edit affordance on the home page, where the full `ProjectDetailsResponse` isn't loaded.
 */
export function useProjectListItemPermissions(item: Pick<ProjectListItem, "owner" | "is_locked" | "locked_by"> | null | undefined): ProjectPermissions {
  const currentUserEmail = useCurrentUserEmail();

  return useMemo(
    () => computeProjectPermissions(currentUserEmail, item?.owner, item?.is_locked, item?.locked_by),
    [currentUserEmail, item?.owner, item?.is_locked, item?.locked_by]
  );
}
